import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';

import { convertToMoney } from '../utils/formats';
import { validateSession } from '../utils/functions';
import { matchesSearchTerms } from '../utils/searchTerms';
import { conditionLabel } from '../utils/usedGame';
import {
    VALIDEZ_DIAS_DEFAULT, CONDICIONES_DEFAULT,
    resolverRenglones, calcularPresupuesto, cantidadValida,
    buildPresupuestoHtml, tituloDelArchivo, imprimirPresupuesto,
} from '../utils/presupuesto';

var config = process.env;

// El presupuesto no se guarda en el sistema, pero el borrador sí sobrevive a ir a mirar un
// precio y volver: mismo criterio que el historial de Consulta de precio.
const BORRADOR_KEY = 'presupuestoBorrador';

// Una hoja A4 a 96 dpi. La vista previa es la hoja REAL (el mismo HTML que se imprime) metida
// en un iframe de ese tamaño y achicada con `scale` para que entre en la columna.
const ANCHO_A4_PX = 794;
const ALTO_A4_PX = 1123;

const BORRADOR_VACIO = {
    renglones: [],
    cliente: '',
    titulo: '',
    validezDias: String(VALIDEZ_DIAS_DEFAULT),
    descuentoPct: '',
    condiciones: CONDICIONES_DEFAULT.join('\n'),
};

class PresupuestosPage extends Component {

    constructor() {
        super();
        this.state = {
            items: [],
            cargando: true,
            suggestions: [],
            ...BORRADOR_VACIO,
            libreNombre: '',
            librePrecio: '',
            previewHtml: '',
            escala: 0.5,
            altoHoja: ALTO_A4_PX,
        };
        this.catalogo = new Map();
        this.inputRef = React.createRef();
        this.previewRef = React.createRef();
        this.previewWrapRef = React.createRef();
        this.imprimirAlCargar = false;
    }

    async componentDidMount() {
        // `true`: con la caja cerrada igual se puede presupuestar (no se cobra nada acá).
        let role = await validateSession(true);
        this.setState({ checked: true, role: role, ...this.leerBorrador() }, () => {
            this.medirVistaPrevia();
            this.actualizarVistaPrevia();
        });
        window.addEventListener('resize', this.medirVistaPrevia);
        this.loadProducts();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.medirVistaPrevia);
        clearTimeout(this.previewTimer);
    }

    leerBorrador = () => {
        try {
            const guardado = JSON.parse(sessionStorage.getItem(BORRADOR_KEY) || 'null');
            if (!guardado || !Array.isArray(guardado.renglones)) {
                return {};
            }
            const borrador = {};
            Object.keys(BORRADOR_VACIO).forEach((campo) => {
                if (guardado[campo] !== undefined) {
                    borrador[campo] = guardado[campo];
                }
            });
            return borrador;
        } catch (e) {
            return {};
        }
    }

    guardarBorrador = () => {
        try {
            const borrador = {};
            Object.keys(BORRADOR_VACIO).forEach((campo) => { borrador[campo] = this.state[campo]; });
            sessionStorage.setItem(BORRADOR_KEY, JSON.stringify(borrador));
        } catch (e) {
            // sessionStorage lleno o bloqueado: el presupuesto en pantalla sigue funcionando.
        }
    }

    // Todo cambio del presupuesto pasa por acá: guarda el borrador y refresca la hoja.
    cambiar = (parcial, despues) => {
        this.setState(parcial, () => {
            this.guardarBorrador();
            this.programarVistaPrevia();
            if (despues) {
                despues();
            }
        });
    }

    loadProducts = () => {
        var request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            }
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'product/all', request)
        .then(results => { return results.json(); })
        .then(data => {
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
            } else {
                // Gastronomía (4) e insumos (5) no se presupuestan: son de la cafetería.
                const items = (data.data || []).filter((p) => ![4, 5].includes(Number(p.productType)));
                this.catalogo = new Map(items.map((p) => [String(p.code), p]));
                this.setState({ items: items, cargando: false }, () => {
                    this.actualizarVistaPrevia();
                    if (this.inputRef.current) {
                        this.inputRef.current.focus();
                    }
                });
            }
        })
        .catch(() => {
            this.setState({ cargando: false });
            alert("No se pudo cargar el catálogo. Probá de nuevo.");
        });
    }

    // ---------------------------------------------------------------- vista previa

    medirVistaPrevia = () => {
        const caja = this.previewWrapRef.current;
        if (caja && caja.clientWidth > 0) {
            this.setState({ escala: caja.clientWidth / ANCHO_A4_PX });
        }
    }

    renglonesResueltos = () => resolverRenglones(this.state.renglones, this.catalogo);

    armarHtml = () => buildPresupuestoHtml({
        cliente: this.state.cliente,
        titulo: this.state.titulo,
        fecha: new Date(),
        validezDias: this.state.validezDias,
        renglones: this.renglonesResueltos(),
        descuentoPct: this.state.descuentoPct,
        condiciones: String(this.state.condiciones || '').split('\n'),
        assetsBase: window.location.origin,
    });

    actualizarVistaPrevia = () => {
        this.setState({ previewHtml: this.armarHtml() });
    }

    // Con retardo: recargar el iframe en cada tecla del nombre del cliente lo hace parpadear.
    programarVistaPrevia = () => {
        clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(this.actualizarVistaPrevia, 250);
    }

    onPreviewLoad = () => {
        const frame = this.previewRef.current;
        try {
            const alto = frame.contentDocument.documentElement.scrollHeight;
            this.setState({ altoHoja: Math.max(ALTO_A4_PX, alto) });
        } catch (e) {
            // noop: sin acceso al documento la vista previa queda en una hoja
        }
        if (this.imprimirAlCargar) {
            this.imprimirAlCargar = false;
            this.lanzarImpresion();
        }
    }

    lanzarImpresion = () => {
        imprimirPresupuesto(this.previewRef.current, tituloDelArchivo({ cliente: this.state.cliente, fecha: new Date() }));
    }

    // Se imprime el iframe de la vista previa: lo que se ve es exactamente lo que sale. Si
    // había un cambio todavía sin dibujar (el retardo de arriba), primero se recarga la hoja y
    // se imprime recién cuando terminó de cargar.
    imprimir = () => {
        clearTimeout(this.previewTimer);
        const html = this.armarHtml();
        if (html === this.state.previewHtml) {
            this.lanzarImpresion();
            return;
        }
        this.imprimirAlCargar = true;
        this.setState({ previewHtml: html });
    }

    // ---------------------------------------------------------------- buscador

    normalizeBarcode = (value) => String(value ?? '').trim();

    debounce = (func, delay) => {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    };

    clearInput = () => {
        const input = this.inputRef.current;
        if (input) {
            input.value = '';
            input.focus();
        }
    }

    // Un solo input para la pistola y para el nombre, como en Consulta de precio. Acá tampoco
    // se filtra el stock 0: lo que no hay se agrega igual y sale como "Sin stock" en la hoja.
    handleInputChange = this.debounce((event) => {
        const typed = String(event.target.value ?? '');
        const scanned = this.normalizeBarcode(typed);
        if (scanned === '' || scanned === '0') {
            this.setState({ suggestions: [] });
            return;
        }
        const matches = this.state.items.filter((element) => {
            const barCode = this.normalizeBarcode(element.barCode);
            return barCode !== '' && barCode !== '0' && barCode === scanned;
        });
        if (matches.length === 1) {
            this.agregarProducto(matches[0]);
            return;
        }
        if (matches.length > 1) {
            // El usado lleva el mismo código de barras que la copia nueva: que elija.
            this.setState({ suggestions: matches.slice(0, 8) });
            return;
        }
        // OJO: no limpiar el input acá — la pistola dispara un onChange por carácter y los
        // prefijos todavía no matchean (ENI-202).
        if (typed.trim().length < 2) {
            this.setState({ suggestions: [] });
            return;
        }
        const suggestions = this.state.items
            .filter((element) => matchesSearchTerms([element.name, `${element.prefix}${element.code}`], typed))
            .slice(0, 8);
        this.setState({ suggestions: suggestions });
    }, 150);

    // ---------------------------------------------------------------- renglones

    agregarProducto = (producto) => {
        const code = String(producto.code);
        const yaEsta = this.state.renglones.some((r) => r.code === code);
        const renglones = yaEsta
            ? this.state.renglones.map((r) => (r.code === code ? { ...r, cantidad: cantidadValida(r.cantidad) + 1 } : r))
            : [...this.state.renglones, { key: `p-${code}`, code: code, nombre: producto.name, cantidad: 1, precioManual: null, cotizarIgual: false }];
        this.cambiar({ renglones: renglones, suggestions: [] }, this.clearInput);
    }

    agregarLibre = () => {
        const nombre = this.state.libreNombre.trim();
        if (nombre === '') {
            return;
        }
        const precio = Math.round(Number(this.state.librePrecio));
        const renglon = {
            key: `l-${Date.now()}`,
            code: null,
            nombre: nombre,
            cantidad: 1,
            precioManual: Number.isFinite(precio) && precio > 0 ? precio : null,
            cotizarIgual: false,
        };
        this.cambiar({ renglones: [...this.state.renglones, renglon], libreNombre: '', librePrecio: '' });
    }

    cambiarRenglon = (key, parcial) => {
        this.cambiar({ renglones: this.state.renglones.map((r) => (r.key === key ? { ...r, ...parcial } : r)) });
    }

    quitarRenglon = (key) => {
        this.cambiar({ renglones: this.state.renglones.filter((r) => r.key !== key) });
    }

    vaciar = () => {
        if (this.state.renglones.length > 0 && !window.confirm('¿Vaciar el presupuesto y empezar uno nuevo?')) {
            return;
        }
        this.cambiar({ ...BORRADOR_VACIO, libreNombre: '', librePrecio: '' }, this.clearInput);
    }

    // ---------------------------------------------------------------- dibujo

    drawSuggestions = () => {
        if (this.state.suggestions.length === 0) {
            return null;
        }
        return (
            <div className="list-group" style={{ position: 'absolute', zIndex: 100, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                {this.state.suggestions.map((element) => (
                    <button
                        type="button"
                        key={`${element.prefix}${element.code}`}
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        onClick={() => this.agregarProducto(element)}
                    >
                        <span>
                            {element.name}
                            {Number(element.productType) === 3 && <span className="badge badge-warning ml-1">USADO{element.condition ? ` · ${conditionLabel(element.condition)}` : ''}</span>}
                            {Number(element.stock) > 0 ? null : <span className="badge badge-secondary ml-1">SIN STOCK</span>}
                            <small className="text-muted d-block">{element.prefix}{element.code} · Stock {Number(element.stock) || 0}</small>
                        </span>
                        <strong>$ {convertToMoney(element.price)}</strong>
                    </button>
                ))}
            </div>
        );
    }

    drawEstado = (renglon) => {
        if (renglon.libre) {
            return renglon.cotiza
                ? <small className="text-muted">Fuera de catálogo</small>
                : <span className="badge badge-secondary">SIN STOCK</span>;
        }
        if (renglon.precio <= 0) {
            // Una ficha en $0 no se puede cotizar: hay que cargarle el precio en Productos.
            return <span className="badge badge-danger">SIN PRECIO</span>;
        }
        if (renglon.alcanza) {
            return <small className="text-muted">Stock {renglon.stock}</small>;
        }
        return (
            <span>
                <span className="badge badge-secondary">{renglon.stock > 0 ? `HAY ${renglon.stock}` : 'SIN STOCK'}</span>
                <label className="d-block mb-0 mt-1" style={{ fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input
                        type="checkbox"
                        checked={renglon.cotizarIgual}
                        onChange={(e) => this.cambiarRenglon(renglon.key, { cotizarIgual: e.target.checked })}
                    /> Cotizar igual
                </label>
            </span>
        );
    }

    drawRenglones = (resueltos) => {
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <span>Juegos del presupuesto</span>
                    {resueltos.length > 0 && (
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={this.vaciar}>Vaciar</button>
                    )}
                </div>
                <div className="card-body p-0">
                    <table className="table mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Juego</th>
                                <th style={{ width: '17%' }}>Stock</th>
                                <th style={{ width: '13%' }}>Cant.</th>
                                <th style={{ width: '16%', textAlign: 'right' }}>Precio</th>
                                <th style={{ width: '16%', textAlign: 'right' }}>Subtotal</th>
                                <th style={{ width: '6%' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {resueltos.length === 0 ? (
                                <tr><td colSpan="6" style={{ textAlign: 'center', color: '#5a6169' }}>Bipeá un juego o buscalo por nombre para agregarlo.</td></tr>
                            ) : resueltos.map((renglon, indice) => {
                                // Lo tipeado, sin normalizar: si el input mostrara el valor ya
                                // corregido, borrar la cantidad para escribir otra la volvería a 1
                                // en el acto y el "3" que sigue quedaría como "13".
                                const tipeado = this.state.renglones[indice] || {};
                                return (
                                <tr key={renglon.key} style={renglon.cotiza ? null : { color: '#868e96' }}>
                                    <td>
                                        {renglon.nombre} {renglon.usado && <span className="badge badge-warning ml-1">USADO</span>}
                                        {renglon.codigo && <small className="text-muted d-block">{renglon.codigo}</small>}
                                    </td>
                                    <td>{this.drawEstado(renglon)}</td>
                                    <td>
                                        <input
                                            type="number"
                                            min="1"
                                            className="form-control form-control-sm"
                                            aria-label={`Cantidad de ${renglon.nombre}`}
                                            value={tipeado.cantidad ?? ''}
                                            onChange={(e) => this.cambiarRenglon(renglon.key, { cantidad: e.target.value })}
                                            onBlur={(e) => this.cambiarRenglon(renglon.key, { cantidad: cantidadValida(e.target.value) })}
                                        />
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {renglon.libre ? (
                                            <input
                                                type="number"
                                                min="0"
                                                className="form-control form-control-sm"
                                                style={{ textAlign: 'right' }}
                                                placeholder="Precio"
                                                aria-label={`Precio de ${renglon.nombre}`}
                                                value={tipeado.precioManual ?? ''}
                                                onChange={(e) => this.cambiarRenglon(renglon.key, { precioManual: e.target.value === '' ? null : e.target.value })}
                                            />
                                        ) : (renglon.precio > 0 ? `$ ${convertToMoney(renglon.precio)}` : '—')}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {renglon.cotiza ? <strong>$ {convertToMoney(renglon.precio * renglon.cantidad)}</strong> : '—'}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button type="button" className="btn btn-link btn-sm p-0 text-danger" title="Quitar" aria-label={`Quitar ${renglon.nombre}`} onClick={() => this.quitarRenglon(renglon.key)}>
                                            <i className="material-icons">close</i>
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="card-footer border-top">
                    <small className="text-muted d-block mb-2">¿No está en el catálogo? Agregalo a mano: sin precio sale como "Sin stock".</small>
                    <div className="form-row">
                        <div className="col-7">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Nombre del juego"
                                value={this.state.libreNombre}
                                onChange={(e) => this.setState({ libreNombre: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') { this.agregarLibre(); } }}
                            />
                        </div>
                        <div className="col-3">
                            <input
                                type="number"
                                min="0"
                                className="form-control form-control-sm"
                                placeholder="Precio (opcional)"
                                value={this.state.librePrecio}
                                onChange={(e) => this.setState({ librePrecio: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') { this.agregarLibre(); } }}
                            />
                        </div>
                        <div className="col-2">
                            <button type="button" className="btn btn-outline-primary btn-sm btn-block" disabled={this.state.libreNombre.trim() === ''} onClick={this.agregarLibre}>Agregar</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    drawDatos = () => {
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">Datos del presupuesto</div>
                <div className="card-body p-3">
                    <div className="form-row">
                        <div className="form-group col-md-6">
                            <label htmlFor="presupuestoCliente">Para</label>
                            <input id="presupuestoCliente" type="text" className="form-control" placeholder="Nombre del cliente o la institución"
                                value={this.state.cliente} onChange={(e) => this.cambiar({ cliente: e.target.value })} />
                        </div>
                        <div className="form-group col-md-6">
                            <label htmlFor="presupuestoTitulo">Título <small className="text-muted">(opcional)</small></label>
                            <input id="presupuestoTitulo" type="text" className="form-control"
                                placeholder={this.state.cliente.trim() ? `Presupuesto para ${this.state.cliente.trim()}` : 'Presupuesto'}
                                value={this.state.titulo} onChange={(e) => this.cambiar({ titulo: e.target.value })} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group col-md-6">
                            <label htmlFor="presupuestoValidez">Validez (días)</label>
                            <input id="presupuestoValidez" type="number" min="0" className="form-control"
                                value={this.state.validezDias} onChange={(e) => this.cambiar({ validezDias: e.target.value })} />
                            <small className="text-muted">En 0 no se imprime el vencimiento.</small>
                        </div>
                        <div className="form-group col-md-6">
                            <label htmlFor="presupuestoDescuento">Descuento general (%)</label>
                            <input id="presupuestoDescuento" type="number" min="0" max="100" step="0.5" className="form-control" placeholder="Sin descuento"
                                value={this.state.descuentoPct} onChange={(e) => this.cambiar({ descuentoPct: e.target.value })} />
                        </div>
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor="presupuestoCondiciones">Condiciones <small className="text-muted">(una por renglón)</small></label>
                        <textarea id="presupuestoCondiciones" className="form-control" rows="3"
                            value={this.state.condiciones} onChange={(e) => this.cambiar({ condiciones: e.target.value })} />
                    </div>
                </div>
            </div>
        );
    }

    drawResumen = (cuenta) => {
        return (
            <div className="card card-small mb-4">
                <div className="card-body p-3">
                    <div className="d-flex justify-content-between">
                        <span>Subtotal <small className="text-muted">· {cuenta.cotizados} {cuenta.cotizados === 1 ? 'juego' : 'juegos'}</small></span>
                        <span>$ {convertToMoney(cuenta.subtotal)}</span>
                    </div>
                    {cuenta.descuento > 0 && (
                        <div className="d-flex justify-content-between text-success">
                            <span>Descuento <small>· {String(cuenta.descuentoPct).replace('.', ',')} %</small></span>
                            <span>− $ {convertToMoney(cuenta.descuento)}</span>
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-baseline border-top mt-2 pt-2">
                        <strong>Total</strong>
                        <strong style={{ fontSize: '24px' }}>$ {convertToMoney(cuenta.total)}</strong>
                    </div>
                    {cuenta.sinStock > 0 && (
                        <small className="text-muted d-block mt-1">
                            {cuenta.sinStock} {cuenta.sinStock === 1 ? 'juego queda' : 'juegos quedan'} afuera del total por falta de stock.
                        </small>
                    )}
                    <button type="button" className="btn btn-primary btn-block mt-3" disabled={this.state.renglones.length === 0} onClick={this.imprimir}>
                        <i className="material-icons">print</i> Imprimir / Guardar PDF
                    </button>
                    <small className="text-muted d-block mt-2">
                        Para el PDF, en el diálogo de impresión elegí "Guardar como PDF" como destino.
                    </small>
                </div>
            </div>
        );
    }

    drawVistaPrevia = () => {
        const { escala, altoHoja } = this.state;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">Vista previa</div>
                <div className="card-body p-3" style={{ background: '#eef2f7' }}>
                    <div ref={this.previewWrapRef} style={{ position: 'relative', width: '100%', height: `${Math.round(altoHoja * escala)}px`, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 6px rgba(22,32,46,0.18)' }}>
                        {/* Sin `allow-scripts`, como las otras hojas que se imprimen: el documento es
                            sólo texto, y `allow-same-origin` es lo que deja disparar print() desde acá. */}
                        <iframe
                            title="Vista previa del presupuesto"
                            ref={this.previewRef}
                            sandbox="allow-same-origin allow-modals"
                            srcDoc={this.state.previewHtml}
                            onLoad={this.onPreviewLoad}
                            style={{ position: 'absolute', top: 0, left: 0, width: `${ANCHO_A4_PX}px`, height: `${altoHoja}px`, border: 0, transform: `scale(${escala})`, transformOrigin: '0 0' }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const resueltos = this.renglonesResueltos();
        const cuenta = calcularPresupuesto(resueltos, this.state.descuentoPct);
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={54} role={this.state.role}/>
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4" style={{ paddingBottom: '40px' }}>
                            <HeaderComponent title="Venta" subtitle="Presupuestos"/>
                            <div className="row">
                                <div className="col-lg-7">
                                    <div className="card card-small mb-4">
                                        <div className="card-body p-3">
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    autoComplete="off"
                                                    className="form-control form-control-lg"
                                                    type="text"
                                                    placeholder={this.state.cargando ? 'Cargando el catálogo…' : 'Bipeá el código de barras o buscá por nombre'}
                                                    disabled={this.state.cargando}
                                                    ref={this.inputRef}
                                                    onChange={(e) => this.handleInputChange(e)}
                                                />
                                                {this.drawSuggestions()}
                                            </div>
                                        </div>
                                    </div>
                                    {this.drawRenglones(resueltos)}
                                    {this.drawDatos()}
                                </div>
                                <div className="col-lg-5">
                                    {this.drawResumen(cuenta)}
                                    {this.drawVistaPrevia()}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default PresupuestosPage;
